import { useEffect, useRef, useState } from 'react'
import { GRAPH_VIEWPORT_TRANSITION_DURATION_MS } from './graphConstants'
import type { ExtrapolatedGraphBottomAxisLabel } from './types'

const PAST_YEAR_OPACITY = 0.8
const PAST_YEAR_FILL = '#aaa'
// Estimated rendered width of a 4-digit year label at fontSize=12. Used to
// translate a right-anchored visual position into a start-anchored x so that
// the OLD rightmost label can slide rightward as a whole when scrolling up
// (rather than being progressively clipped from its right edge).
const RIGHT_LABEL_WIDTH_ESTIMATE_PX = 30
const RIGHT_LABEL_SLIDE_OFF_DISTANCE_PX = RIGHT_LABEL_WIDTH_ESTIMATE_PX + 18

type AxisYearsSnapshot = {
  oldCurrentYear: number
  oldCurrentYearX: number | null
  oldRightYear: number
  oldRightLabelInitialX: number | null
  startTimeMs: number
}

type UseTransitioningAxisLabelsParams = {
  baseLabels: ExtrapolatedGraphBottomAxisLabel[]
  currentYear: number
  transitionTriggerYear: number
  xForTimestamp: (timestamp: number) => number
}

const getRightmostLabelYear = (labels: ExtrapolatedGraphBottomAxisLabel[]): number | null => {
  const rightmostLabel = labels[labels.length - 1]
  if (!rightmostLabel) return null
  const parsedYear = Number.parseInt(rightmostLabel.text, 10)
  return Number.isNaN(parsedYear) ? null : parsedYear
}

export const useTransitioningAxisLabels = ({
  baseLabels,
  currentYear,
  transitionTriggerYear,
  xForTimestamp,
}: UseTransitioningAxisLabelsParams): ExtrapolatedGraphBottomAxisLabel[] => {
  const [previousTriggerYear, setPreviousTriggerYear] = useState(transitionTriggerYear)
  const [snapshot, setSnapshot] = useState<AxisYearsSnapshot | null>(null)
  const [, setRenderToken] = useState(0)
  const previousBaseLabelsRef = useRef(baseLabels)

  if (transitionTriggerYear !== previousTriggerYear) {
    // The trigger fires when the *target* year changes (i.e. the user just
    // initiated a scroll). The viewport target already reflects the new state,
    // so capture the old x positions from the previous rendered label set.
    // With no labels at all (currentYearPill mode) there is nothing to fade —
    // skip the snapshot, or the fallbacks below would fade a phantom year.
    const previousBaseLabels = previousBaseLabelsRef.current
    if (previousBaseLabels.length > 0 || baseLabels.length > 0) {
      const oldCurrentYearLabel = previousBaseLabels.find(
        label => label.text === `${currentYear}`,
      )
      const rightmostLabel = previousBaseLabels[previousBaseLabels.length - 1]
      const rightmostYear = getRightmostLabelYear(previousBaseLabels)
      setSnapshot({
        oldCurrentYear: currentYear,
        oldCurrentYearX: oldCurrentYearLabel?.x ?? null,
        oldRightYear: rightmostYear ?? currentYear,
        oldRightLabelInitialX: rightmostLabel?.x ?? null,
        startTimeMs: performance.now(),
      })
    }
    setPreviousTriggerYear(transitionTriggerYear)
  }

  useEffect(() => {
    previousBaseLabelsRef.current = baseLabels
  }, [baseLabels])

  useEffect(() => {
    if (!snapshot) return
    let rafId = 0
    const tick = () => {
      const elapsedMs = performance.now() - snapshot.startTimeMs
      if (elapsedMs >= GRAPH_VIEWPORT_TRANSITION_DURATION_MS) {
        setSnapshot(null)
        return
      }
      setRenderToken(token => token + 1)
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => {
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [snapshot])

  if (!snapshot) return baseLabels

  const elapsedMs = performance.now() - snapshot.startTimeMs
  const transitionProgress = Math.min(
    1,
    elapsedMs / GRAPH_VIEWPORT_TRANSITION_DURATION_MS,
  )
  const oldLabelOpacityFalloff = 1 - transitionProgress

  const adjustedNewLabels: ExtrapolatedGraphBottomAxisLabel[] = baseLabels.map((
    label,
    labelIndex,
  ) => {
    const isCurrentYearLabel = label.text === `${currentYear}`
    const isRightmostLabel = labelIndex === baseLabels.length - 1
    if (!isCurrentYearLabel && !isRightmostLabel) return label
    return {
      ...label,
      key: `${label.key}-fade-in`,
      opacity: (label.opacity ?? 1) * transitionProgress,
    }
  })

  const fadingOldLabels: ExtrapolatedGraphBottomAxisLabel[] = []

  if (snapshot.oldCurrentYearX !== null) {
    fadingOldLabels.push({
      key: `${snapshot.oldCurrentYear}-fade-out-current`,
      x: snapshot.oldCurrentYearX,
      text: `${snapshot.oldCurrentYear}`,
      textAnchor: 'middle',
      opacity: PAST_YEAR_OPACITY * oldLabelOpacityFalloff,
      fill: PAST_YEAR_FILL,
    })
  }

  // When scrolling up (the new rightmost label year is *less* than the old
  // rightmost label year), xForTimestamp would only carry the OLD label
  // rightward as far as the canvas right edge, which doesn't read as "sliding
  // off". Drive the OLD label with a dedicated rightward slide-off animation
  // anchored to where it was rendered at trigger time, using textAnchor='start'
  // so the text translates as a whole rather than getting progressively clipped
  // from its right edge while right-anchored.
  const oldRightYearTimestampMs = Date.UTC(snapshot.oldRightYear, 0, 1)
  const newRightmostYear = getRightmostLabelYear(baseLabels)
  const isOldRightYearSlidingOffCanvas =
    newRightmostYear !== null
    && snapshot.oldRightYear > newRightmostYear
    && snapshot.oldRightLabelInitialX !== null
  if (isOldRightYearSlidingOffCanvas && snapshot.oldRightLabelInitialX !== null) {
    const slideStartX = snapshot.oldRightLabelInitialX - RIGHT_LABEL_WIDTH_ESTIMATE_PX
    const slideX = slideStartX + RIGHT_LABEL_SLIDE_OFF_DISTANCE_PX * transitionProgress
    fadingOldLabels.push({
      key: `${snapshot.oldRightYear}-fade-out-right`,
      x: slideX,
      text: `${snapshot.oldRightYear}`,
      textAnchor: 'start',
      opacity: PAST_YEAR_OPACITY * oldLabelOpacityFalloff,
      fill: PAST_YEAR_FILL,
    })
  } else {
    fadingOldLabels.push({
      key: `${snapshot.oldRightYear}-fade-out-right`,
      x: xForTimestamp(oldRightYearTimestampMs),
      text: `${snapshot.oldRightYear}`,
      textAnchor: 'end',
      opacity: PAST_YEAR_OPACITY * oldLabelOpacityFalloff,
      fill: PAST_YEAR_FILL,
    })
  }

  return [...fadingOldLabels, ...adjustedNewLabels]
}
